import { Injectable } from '@angular/core';
import {
  Auth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithPhoneNumber,
  linkWithCredential,
  PhoneAuthProvider,
  RecaptchaVerifier,
  User as FirebaseUser,
  signInWithCredential
} from '@angular/fire/auth';
import {
  signInWithRedirect,
  getRedirectResult
} from '@angular/fire/auth';

import {
  Firestore,
  doc,
  getDoc,
  setDoc,
  updateDoc
} from '@angular/fire/firestore';
import { BehaviorSubject, Observable, filter, firstValueFrom, map, take } from 'rxjs';
import { User } from '../models/user.model';
import { environment } from 'src/environments/environment';
declare global {
  interface Window {
    google: any;
  }
}
@Injectable({ providedIn: 'root' })
export class AuthService {


  private _user$ = new BehaviorSubject<User | null>(null);
  private authInitialized = false;

  constructor(
    private auth: Auth,
    private firestore: Firestore
  ) {

    onAuthStateChanged(this.auth, async (firebaseUser) => {
      if (!firebaseUser) {
        this._user$.next(null);
        this.authInitialized = true;
        return;
      }

      const user = await this.loadOrCreateUser(firebaseUser);
      this._user$.next(user);
      this.authInitialized = true;
    });
  }

  /* ------------------------------------------------------------------
   * Public streams & getters
   * ------------------------------------------------------------------ */

  get user$(): Observable<User | null> {
    return this._user$.asObservable();
  }

  get currentUser(): User | null {
    return this._user$.value;
  }

  get firebaseUid(): string | null {
    return this.auth.currentUser?.uid ?? null;
  }

private async getUserStatus(user: FirebaseUser): Promise<'NEW_USER' | 'EXISTING_USER'> {
  const ref = doc(this.firestore, 'users', user.uid);
  const snap = await getDoc(ref);

  return snap.exists() ? 'EXISTING_USER' : 'NEW_USER';
}

private isMobile(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}


async handleGoogleRedirect(): Promise<'NEW_USER' | 'EXISTING_USER' | null> {
  const result = await getRedirectResult(this.auth);

  if (!result) {
    return null;
  }

  return this.getUserStatus(result.user);
}
 initGoogleOneTap(): Promise<'NEW_USER' | 'EXISTING_USER'> {
  return new Promise((resolve, reject) => {
    if (!window.google) {
      reject(new Error('Google GSI not loaded'));
      return;
    }

    window.google.accounts.id.initialize({
      client_id: environment.googleClientId,
      context: 'signin',
      ux_mode: 'popup',
      auto_select: false,
      itp_support: true,
  use_fedcm_for_prompt: false,
      callback: async (response: any) => {
        try {
          // 1. Authenticate with Firebase
          const credential = GoogleAuthProvider.credential(
            response.credential
          );

          const result = await signInWithCredential(this.auth, credential);

          // 2. Determine new vs existing user
          const uid = result.user.uid;
          const ref = doc(this.firestore, 'users', uid);
          const snap = await getDoc(ref);

          if (!snap.exists()) {
            resolve('NEW_USER');
          } else {
            resolve('EXISTING_USER');
          }

        } catch (err) {
          reject(err);
        }
      }
    });

    window.google.accounts.id.prompt((notification: any) => {
      if (notification.isNotDisplayed()) {
        reject(
          new Error(
            `One Tap not displayed: ${notification.getNotDisplayedReason()}`
          )
        );
      }
    });
  });
}

  hasRole(role: string) {
  return this.user$.pipe(
    map(u => u?.role === role)
  );
}

  async waitForUser(): Promise<User | null> {
  if (this.authInitialized && this._user$.value) {
    return this._user$.value;
  }

  if (this.authInitialized && !this.auth.currentUser) {
    return null;
  }

  return firstValueFrom(
    this.user$.pipe(
      filter((user): user is User => user !== null)
    )
  );
}

  /* ------------------------------------------------------------------
   * Authentication
   * ------------------------------------------------------------------ */

  async register(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    phone: string
  ): Promise<FirebaseUser> {
    const cred = await createUserWithEmailAndPassword(this.auth, email, password);
    await this.createUserDocument(cred.user, { firstName, lastName,phone});
    return cred.user;
  }

  async login(email: string, password: string): Promise<void> {
    await signInWithEmailAndPassword(this.auth, email, password);
  }
  async loginWithGoogle(): Promise<'NEW_USER' | 'EXISTING_USER'> {
    const provider = new GoogleAuthProvider();
  
    provider.setCustomParameters({
      prompt: 'select_account'
    });
  
    const credential = await signInWithPopup(
      this.auth,
      provider
    );
  
    const uid = credential.user.uid;
  
    const userDocRef = doc(
      this.firestore,
      'users',
      uid
    );
  
    const userSnap = await getDoc(userDocRef);
  
    if (!userSnap.exists()) {
      return 'NEW_USER';
    }
  
    return 'EXISTING_USER';
  }
  


  async logout(): Promise<void> {
    await signOut(this.auth);
    this._user$.next(null);
  }

async completeGoogleRegistration(data: {
  firstName: string;
  lastName: string;
  phone?: string;
}) {
  const user = this.auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  await setDoc(doc(this.firestore, 'users', user.uid), {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    firstName: data.firstName,
    lastName: data.lastName,
    phone: data.phone || null,
    role: 'tenant',
    createdAt: Date.now(),
    leases: [],
    payments: [],
    documents: [],
    subscriptions: [],
    monthlyRent: 0,
    landlordId: null,
    rentalId: null,
    pendingLandlordId: null,
    pendingRentalId:null,
    status: 'pending'
  });
  return user;
}

  private async loadOrCreateUser(firebaseUser: FirebaseUser): Promise<User> {
  const ref = doc(this.firestore, `users/${firebaseUser.uid}`);
  const snap = await getDoc(ref);

  if (snap.exists()) {
    const data = snap.data() as User;

    // 🔒 FORCE UID from Firebase Auth
    return {
      ...data,
      uid: firebaseUser.uid,
    };
  }

  return this.createUserDocument(firebaseUser);
}

  private async createUserDocument(
    firebaseUser: FirebaseUser,
    profile?: Partial<User>
  ): Promise<User> {

    const user: User = {
      uid: firebaseUser.uid,
      email: firebaseUser.email ?? '',
      role: 'tenant',
      firstName: profile?.firstName ?? '',
      lastName: profile?.lastName ?? '',
      createdAt: Date.now(),
      leases: [],
      payments: [],
      documents: [],
      subscriptions: [],
      monthlyRent: 0,
      landlordId: 'rci',
      rentalId: null,
      pendingRentalId: null,
      pendingLandlordId: 'rci',
      tenantId: null
    };

    await setDoc(doc(this.firestore, `users/${firebaseUser.uid}`), user);
    return user;
  }
  /** Synchronous helpers (safe after user loaded) */
    get landlordId(): string | null {
      // DO NOT subscribe — pull last value safely
      let value: string | null = null;
      this.user$.pipe(take(1)).subscribe(u => value = u?.landlordId ?? null);
      return value;
    }
  async refreshCurrentUser(): Promise<void> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) return;

    const snap = await getDoc(
      doc(this.firestore, `users/${firebaseUser.uid}`)
    );

    if (snap.exists()) {
      const data = snap.data() as User;

      this._user$.next({
        ...data,
        uid: firebaseUser.uid,
      });
    }
  }
  get landlordScope$(): Observable<string[]> {
  return this.user$.pipe(
    map(user => {
      if (!user) return [];

      if (user.role === 'landlord' && user.landlordId) {
        return [user.landlordId];
      }

      if (user.role === 'manager') {
        return user.managedLandlordIds ?? [];
      }

      return [];
    })
  );
}

  async updateUserProfile(
    data: Partial<Pick<User, 'firstName' | 'lastName' | 'phone'>>
  ): Promise<void> {

    const uid = this.firebaseUid;
    if (!uid) throw new Error('User not authenticated');

    await updateDoc(doc(this.firestore, `users/${uid}`), data);

    const cached = this._user$.value;
    if (cached) {
      this._user$.next({ ...cached, ...data });
    }
  }

  async updateUserData(uid: string, data: Partial<User>): Promise<void> {
    await updateDoc(doc(this.firestore, `users/${uid}`), data);

    if (this._user$.value?.uid === uid) {
      this._user$.next({ ...this._user$.value, ...data });
    }
  }

  /* ------------------------------------------------------------------
   * Role helpers
   * ------------------------------------------------------------------ */

  isAdmin(): boolean {
    return this._user$.value?.role === 'admin';
  }

  isTenant(): boolean {
    return this._user$.value?.role === 'tenant';
  }

  async getUserRole(): Promise<string> {
    const user = await this.waitForUser();
    if (!user) throw new Error('User not authenticated');
    return user.role;
  }

  /* ------------------------------------------------------------------
   * Phone / MFA
   * ------------------------------------------------------------------ */

  setupRecaptcha(containerId: string): RecaptchaVerifier {
    return new RecaptchaVerifier(this.auth, containerId, { size: 'invisible' });
  }

  async linkPhoneNumber(phone: string, containerId: string): Promise<void> {
    if (!this.auth.currentUser) throw new Error('User not authenticated');

    const verifier = this.setupRecaptcha(containerId);
    const confirmation = await signInWithPhoneNumber(this.auth, phone, verifier);

    const code = prompt('Enter verification code');
    if (!code) throw new Error('Verification code required');

    const credential = PhoneAuthProvider.credential(
      confirmation.verificationId,
      code
    );

    await linkWithCredential(this.auth.currentUser, credential);
    await this.updateUserProfile({ phone });
  }
}
